import Tooltip from '@components/Tooltip';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { flexRender } from '@tanstack/react-table';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';

import { defaultHeadingCellClassName } from '../../../shared/components/table-header/classes';
import { DragHandle } from '../../../shared/components/table-header/DragHandle';
import type { SortableTableHeaderProps } from '../../../shared/components/table-header/types';
import { getAriaSort, isHeaderDraggable, renderSortIcon } from '../../../shared/components/table-header/utils';

/**
 * Sortable table header component with accessibility improvements.
 * Handles both sortable and non-sortable columns with drag-and-drop support.
 */
export function SortableTableHeader<TData, TValue>({ header }: SortableTableHeaderProps<TData, TValue>) {
  const canSort = header.column.getCanSort();
  const sortDirection = header.column.getIsSorted();
  const isDraggable = isHeaderDraggable(header);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: header.id,
    data: {
      type: 'Header',
      header,
    },
    disabled: !isDraggable,
  });

  const columnDef = header.column.columnDef;
  const hasMaxSize = columnDef.maxSize !== undefined && columnDef.maxSize !== Number.MAX_SAFE_INTEGER;
  const hasMinSize = columnDef.minSize !== undefined && columnDef.minSize !== 20;
  const isFlexible = !hasMaxSize && hasMinSize;

  const style = {
    transform: isDraggable ? CSS.Transform.toString(transform) : undefined,
    transition: isDraggable ? transition : undefined,
    opacity: isDragging ? 0.5 : 1,
    ...(hasMaxSize
      ? {
          width: `${columnDef.maxSize}px`,
          minWidth: `${columnDef.maxSize}px`,
          maxWidth: `${columnDef.maxSize}px`,
        }
      : isFlexible
        ? {
            width: '100%',
            minWidth: `${columnDef.minSize}px`,
          }
        : {
            width: `${header.getSize()}px`,
          }),
  };

  return (
    <th
      key={header.id}
      colSpan={header.colSpan}
      scope="col"
      aria-sort={getAriaSort(sortDirection, canSort)}
      ref={setNodeRef}
      style={style}
      className={twMerge(
        defaultHeadingCellClassName,
        '!px-0 !py-0',
        header.column.columnDef?.meta?.headingCellClassName,
        header.column.getIsPinned() === 'right' && 'bg-info-50 sticky right-0 z-10',
        header.column.getIsPinned() === 'left' && 'bg-info-50 sticky left-0 z-10',
        isDragging && 'z-50 opacity-75 shadow-xl',
      )}
    >
      {canSort ? (
        <div
          className={clsx(
            'group inline-flex w-full items-center px-3 py-3.5 text-left',
            header.column.columnDef.meta?.headingLinkClassName,
          )}
        >
          <span className="mr-1">{flexRender(header.column.columnDef.header, header.getContext())}</span>
          <Tooltip content="Sort list by this column">
            <button
              type="button"
              onClick={header.column.getToggleSortingHandler()}
              data-testid={`sort-by-${header.column.columnDef.id}`}
              className="hover:bg-info-100 rounded p-0.5 transition-colors"
              aria-label={`Sort by ${header.column.columnDef.header}`}
            >
              {renderSortIcon(sortDirection)}
            </button>
          </Tooltip>
          <DragHandle isDraggable={isDraggable} listeners={listeners} attributes={attributes} />
        </div>
      ) : (
        <div className="group flex items-center px-3 py-3.5">
          <span className={clsx(isDraggable ? '' : 'mr-2')}>
            {flexRender(header.column.columnDef.header, header.getContext())}
          </span>
          <DragHandle isDraggable={isDraggable} listeners={listeners} attributes={attributes} />
        </div>
      )}
    </th>
  );
}
